import { assertErrors, fetchAPI, METHODS, parseBody } from './common';
import qs from 'qs';

/**
 * ACTIVE referes to food runs which have any status which is neither NULL nor COMPLETED
 * IN_PROGRESS refers to any one of 'PICKUP_IN_PROGRESS' | 'DELIVERY_IN_PROGRESS'.
 * @typedef {'ASSIGNED' | 'PICKUP_IN_PROGRESS' | 'DELIVERY_IN_PROGRESS' | 'COMPLETED' | 'ACTIVE' | 'IN_PROGRESS' | 'WAITING_FOR_REASSIGN'} FoodRunStatus
 */

/**
 * FORWARD means to sort from past to future. For example, Oct. 3rd 2022 would appear before Dec. 12th 2030.
 * BACKWARD means to sort from future to past. For example, Oct. 3rd 2022 would appear after Dec. 12th 2030.
 * @typedef {"FORWARD" | "BACKWARD"} DateOrder
 */

/**
 * @typedef FoodRunFilters
 * @property {FoodRunStatus?} status         Food run status to select. Ignored if falsey (`null`, `undefined`, `false`).
 * @property {boolean}        mine           If true, only select foodruns in which the given user is the runner.
 * @property {boolean}        distance       If true, only select donations that are less than or equal to the `pickup_dist`
 *                                           user setting from the user's location.
 * @property {number}         step           For pagination, this is the page size. Must be >= 1.
 * @property {number}         offset         For pagination, this is the page index. Must be >= 0.
 * @property {FilterModel}    filterModel    A mui DataGrid filter model.
 * @property {string}         timezoneId     The current user's timezone id, used to parsing dates from UTC.
 * @property {boolean}        removePastRuns If true, only select foodruns that has start_date in today or in future.
 * @property {boolean}        related        If true, only select foodruns that has same parentId as the selected parentId.
 * @property {string}         parentId       This is null for other filter types except related. This fetches runs that share the same parent_id
 * @property {DateOrder?}     dateOrder      Ordering of food runs by date. Defaults to "FORWARD" if null or undefined.
 * @property {boolean?}       availableOnly  If true, selects only claimable/available food runs.
 * @property {boolean?}       isExport       If this flag is set to true, the count query is skipped in backend
 */

/**
 * @type FoodRunResult
 * @property {FoodRun[]} results All food runs fitting the given filter and in the requested page.
 * @property {number}    count   Count of all food runs, regardless of pagination, which fit the filter.
 */

/**
 * Get all food runs acessible by the current user with pagination and server-side filtering.
 * @param {FoodRunFilters} filters
 * @returns Promise<FoodRunResult>
 */
export async function get({
  status,
  mine,
  distance,
  step,
  offset,
  filterModel,
  removePastRuns,
  related,
  parentId,
  dateOrder,
  availableOnly = false,
  isExport = false,
  rescueVehicleId,
}) {
  const params = qs.stringify({
    status,
    mine,
    distance,
    step,
    offset,
    removePastRuns,
    related,
    parentId,
    dateOrder,
    availableOnly,
    isExport,
    rescueVehicleId,
  });
  const res = await fetchAPI(`/food-run/get?${params}`, METHODS.post, {
    filterModel,
  });

  const { results, count } = await parseBody(res);
  return { results, count };
}

/**
 * Lightweight listing for the home dashboard "To Be Reassigned" preview card.
 * Returns only the fields that card renders plus the total count, avoiding the
 * heavy column computation of `get`.
 * @param {object} params
 * @param {number} params.offset Page index (>= 0).
 * @param {number} params.step   Page size (>= 1).
 * @returns Promise<{ results: object[], count: number }>
 */
export async function getToBeReassigned({ offset, step }) {
  const params = qs.stringify({ step, offset });
  const res = await fetchAPI(
    `/food-run/to-be-reassigned?${params}`,
    METHODS.get
  );
  const { results, count } = await parseBody(res);
  return { results, count };
}

/**
 * @typedef NewDonation
 * @property {number} vehicleSize 0, 1, 2 => "CAR", "VAN", "TRUCK"
 * @property {number} deliveryOption 0, 1, 2 => "SELF-DELIVER", "RECEIVER-PICKUP", "PAID-PREMIUM-PICKUP"
 * @property {string[]} selectedFoodTypes ["PREPARED" | "PRODUCE" | "NON-PERISHABLE"]
 * @property {Date} startTime
 * @property {Date} endTime
 * @property {string} title
 * @property {string} description
 * @property {number} location.lat
 * @property {number} location.lng
 * @property {string} location.formattedAddress
 * @property {string} location.suiteNumber
 * @property {string} location.city
 * @property {string} location.province
 * @property {string} location.country
 * @property {string} location.zipCode
 * @property {string} location.placeId
 * @property {string} location.place
 * @property {string} location.timeZoneId
 * @property {string[]} images
 */

/**
 * Update food run.
 * @param {FoodRun}
 */
export async function update(data) {
  const res = await fetchAPI('/food-run', METHODS.put, data);
  return parseBody(res);
}

/**
 * Update food run single instance.
 * @param {FoodRun}
 */
export async function updateSingleInstance(data) {
  const res = await fetchAPI('/food-run/single-instance', METHODS.put, data);
  return parseBody(res);
}

/**
 * Get Food run single instance by id
 * @param {FoodRun}
 */
export async function getFoodRunSingleInstanceById(data) {
  const res = await fetchAPI(`/donation/food-run/${data.id}`, METHODS.get);
  return parseBody(res);
}

/**
 * Complete food run.
 * @param {string} rescueId
 * @param {object} foodWeights
 * @param {string} donorTeam
 * @param {string} receiverTeam
 * @param {number} donorRating
 * @param {string} donorRatingNotes
 * @param {number} receiverRating
 * @param {string} receiverRatingNotes
 * @param {boolean} noDonation
 */
export async function completeFoodRun(data) {
  const res = await fetchAPI('/food-run/complete', METHODS.put, data);
  return parseBody(res);
}

/**
 * Update food run completion data (food weights and completion images).
 * @param {string} data.rescueId
 * @param {object} data.foodWeights
 * @param {string[]} data.images
 */
export async function updateCompletionData(data) {
  const res = await fetchAPI('/food-run/completion-data', METHODS.put, data);
  return parseBody(res);
}

/**
 * Creates a new food run.
 * @param {FoodRun} donation
 */
export async function create(data) {
  const res = await fetchAPI('/food-run', METHODS.post, data);
  return parseBody(res);
}

/**
 * Creates a new food run.
 * @param {object} params
 * @param {string} params.id Id (donation id) of the relevant food run
 * @param {number} params.distance Max distance to pickup location to notify drivers.
 */
export async function requestDriversSMS({ distance, id }) {
  const res = await fetchAPI(`/food-run/request-drivers/${id}`, METHODS.post, {
    distance,
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Fetch eligible drivers for a food run within a radius (miles)
 * @param {object} params
 * @param {string} params.id Donation id
 * @param {number} params.radius Radius in miles
 */
export async function getEligibleDrivers({ id, radius }) {
  const params = qs.stringify({ radius });
  const res = await fetchAPI(
    `/food-run/eligible-drivers/${id}?${params}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Request drivers with explicit selection list
 * @param {object} params
 * @param {string} params.id Donation id
 * @param {number} params.distance Distance in miles
 * @param {string[]} [params.driverIds] Selected driver ids
 */
export async function requestDriversSelected({ id, distance, driverIds }) {
  const res = await fetchAPI(`/food-run/request-drivers/${id}`, METHODS.post, {
    distance,
    driverIds,
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Change the recipient of a food run retroactively (up to 30 days after scheduled date).
 * @param {object} params
 * @param {string} params.donationId The donation ID of the food run
 * @param {string} params.newRecipientTeamId The new recipient team ID
 */
export async function changeRecipientRetroactively({
  donationId,
  newRecipientTeamId,
}) {
  const res = await fetchAPI(
    `/food-run/change-recipient/${donationId}`,
    METHODS.put,
    {
      newRecipientTeamId,
    }
  );
  await assertErrors(res);
  return parseBody(res);
}
